import React, { useEffect, useState } from "react";
import VoucherTypeMap from "@/components/migrator/VoucherTypeMap";
import { ZohoSetupGuide, ZOHO_ERRORS } from "@/components/zoho/ZohoSetupGuide";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Upload, Zap, FileText, Plug, PlugZap, RefreshCw, Unplug,
  CheckCircle2, AlertTriangle, Download, ArrowLeftRight,
} from "lucide-react";
import { fmtINR } from "@/lib/format";

const ENTITIES = [
  // Iter-201 · Customers & Vendors are now separate first-class imports so
  // the accountant doesn't have to pre-tag "Contact Type" per row. The
  // combined "Contacts" option stays for legacy Zoho exports that already
  // include the Contact Type column.
  { v: "customers", l: "Customers · Party Master" },
  { v: "vendors", l: "Vendors · Party Master" },
  { v: "contacts", l: "Contacts (combined — legacy Zoho export)" },
  { v: "items", l: "Items / Products" },
  { v: "invoices", l: "Sales Invoices" },
  { v: "bills", l: "Purchase Bills" },
  { v: "credit_notes", l: "Credit Notes" },
  { v: "opening_balances", l: "Day-1 Opening Balances (TB)" },
  // Iter-121 · one-shot detailed GL that carries EVERY transaction
  { v: "detailed_gl", l: "Detailed General Ledger (all transactions)" },
];

const DCS = [
  { v: "in", l: "India · accounts.zoho.in" },
  { v: "com", l: "Global · accounts.zoho.com" },
  { v: "eu", l: "Europe · accounts.zoho.eu" },
  { v: "com.au", l: "Australia · accounts.zoho.com.au" },
  { v: "jp", l: "Japan · accounts.zoho.jp" },
  { v: "ca", l: "Canada · accounts.zoho.ca" },
];

/** Parse a Zoho CSV safely — handles quoted commas + escaped quotes. */
function parseCsv(text) {
  const rows = [];
  let cur = [], cell = "", inQ = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i], n = text[i + 1];
    if (inQ) {
      if (c === '"' && n === '"') { cell += '"'; i++; }
      else if (c === '"') { inQ = false; }
      else { cell += c; }
    } else {
      if (c === '"') { inQ = true; }
      else if (c === ",") { cur.push(cell); cell = ""; }
      else if (c === "\n" || c === "\r") {
        if (c === "\r" && n === "\n") i++;
        cur.push(cell); rows.push(cur); cur = []; cell = "";
      } else { cell += c; }
    }
  }
  if (cell.length || cur.length) { cur.push(cell); rows.push(cur); }
  const nonEmpty = rows.filter(r => r.some(v => (v || "").trim().length));
  if (!nonEmpty.length) return [];
  const [headerRow, ...body] = nonEmpty;
  const headers = headerRow.map(h => h.trim());
  return body.map(r => Object.fromEntries(headers.map((h, i) => [h, (r[i] || "").trim()])));
}

const ACTION_COLOR = {
  match: "text-emerald-500 border-emerald-500",
  create: "text-blue-500 border-blue-500",
  stage: "text-muted-foreground border-muted-foreground",
  unmapped_party: "text-orange-500 border-orange-500",
};

function DiffTable({ entity, mapping }) {
  const [filter, setFilter] = useState("");
  const q = filter.toLowerCase();
  const visible = (mapping || []).filter(m => {
    if (!q) return true;
    return JSON.stringify(m).toLowerCase().includes(q);
  });
  if (!mapping?.length) {
    return <div className="text-xs text-muted-foreground p-4">No rows to reconcile.</div>;
  }

  const isFin = (entity === "invoices" || entity === "bills" || entity === "credit_notes");
  const isOB = entity === "opening_balances";
  const isContacts = entity === "contacts";

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Input placeholder="Filter reconciliation rows…" value={filter}
          onChange={e => setFilter(e.target.value)}
          className="max-w-[280px] text-xs h-8" data-testid="zoho-diff-filter" />
        <span className="text-[10px] text-muted-foreground">
          Showing {visible.length} of {mapping.length}
        </span>
      </div>
      <div className="overflow-x-auto max-h-[440px] overflow-y-auto border border-border">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-card z-10">
            <tr className="border-b">
              <th className="text-left py-2 px-2 uppercase text-[10px]">Zoho (source)</th>
              {isContacts && <th className="text-left uppercase text-[10px]">Type</th>}
              {isContacts && <th className="text-left uppercase text-[10px]">GSTIN</th>}
              {isFin && <th className="text-left uppercase text-[10px]">Party</th>}
              {isFin && <th className="text-right uppercase text-[10px]">Amount</th>}
              {isOB && <th className="text-right uppercase text-[10px]">Debit</th>}
              {isOB && <th className="text-right uppercase text-[10px]">Credit</th>}
              <th className="text-center py-2 px-2 uppercase text-[10px]">
                <ArrowLeftRight className="w-3 h-3 inline" />
              </th>
              <th className="text-left uppercase text-[10px]">Zenith (target)</th>
              <th className="text-left uppercase text-[10px]">Status</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((m, i) => {
              const cls = ACTION_COLOR[m.action] || "text-muted-foreground border-muted-foreground";
              return (
                <tr key={m.row_key || i} className="border-b hover:bg-muted/40" data-testid={`zoho-diff-row-${i}`}>
                  <td className="py-1.5 px-2 font-medium">
                    {m.source_name || m.row_key || "—"}
                  </td>
                  {isContacts && <td className="text-[10px] font-mono uppercase text-muted-foreground">{m.source_type || "—"}</td>}
                  {isContacts && <td className="font-mono text-[10px]">{m.source_gstin || "—"}</td>}
                  {isFin && <td className="text-muted-foreground">{m.source_party || "—"}</td>}
                  {isFin && <td className="text-right font-mono">{fmtINR(m.source_amount || 0)}</td>}
                  {isOB && <td className="text-right font-mono text-emerald-600">{fmtINR(m.source_debit || 0)}</td>}
                  {isOB && <td className="text-right font-mono text-red-500">{fmtINR(m.source_credit || 0)}</td>}
                  <td className="text-center text-muted-foreground">→</td>
                  <td className="font-medium">
                    {m.target_name || "—"}
                    {m.target_id && <span className="ml-2 text-[9px] font-mono uppercase text-emerald-500">✓ EXISTS</span>}
                    {!m.target_id && m.action === "create" && <span className="ml-2 text-[9px] font-mono uppercase text-blue-500">NEW</span>}
                  </td>
                  <td>
                    <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 border ${cls}`}>
                      {m.action}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatCard({ label, value, tone = "" }) {
  const toneClass = tone === "good" ? "text-emerald-600" : tone === "warn" ? "text-orange-500" : "";
  return (
    <div className="border border-border p-3">
      <div className="text-[10px] font-mono uppercase text-muted-foreground">{label}</div>
      <div className={`text-2xl font-mono mt-0.5 ${toneClass}`}>{value}</div>
    </div>
  );
}

export default function ZohoMigrator() {
  const [params, setParams] = useSearchParams();
  const [entity, setEntity] = useState("contacts");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dc, setDc] = useState("in");
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  // Iter-121 · Detailed GL toggle — "include opening balances" (default ON)
  const [includeOpening, setIncludeOpening] = useState(true);

  // Two-stage CSV flow (mirrors Tally): file → preview → commit → result
  const [preview, setPreview] = useState(null);            // {parsed_token, entity, stats, mapping_preview, warnings, mapping_total}
  const [committing, setCommitting] = useState(false);
  const [commitProgress, setCommitProgress] = useState(null); // {pct, stage, note}
  const [result, setResult] = useState(null);
  // Iter-194/195 · Voucher-type mapping picker state — { sourceType: zenithType }
  const [vtypeMap, setVtypeMap] = useState({});
  const [vtypeMapPersistedAt, setVtypeMapPersistedAt] = useState(null);

  // Iter-195 · Auto-load the accountant's last-saved Zoho mapping.
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/migrator/voucher-type-map/zoho");
        if (r.data?.map && Object.keys(r.data.map).length) {
          setVtypeMap(r.data.map);
          setVtypeMapPersistedAt(r.data.updated_at);
        }
      } catch { /* first-time users — nothing saved yet */ }
    })();
  }, []);

  const loadStatus = async () => {
    try { const r = await api.get("/zoho/status"); setStatus(r.data); }
    catch (e) { setStatus({ connected: false, configured: false }); }
  };

  useEffect(() => {
    loadStatus();
    if (params.get("zoho_connected") === "1") {
      setTimeout(() => toast.success("Zoho Books connected successfully."), 300);
      setParams({});
    } else if (params.get("zoho_error")) {
      const code = params.get("zoho_error");
      setTimeout(() => toast.error(ZOHO_ERRORS[code] || `Zoho connection failed: ${code}`, { duration: 10000 }), 300);
      setParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    try {
      const r = await api.get("/zoho/oauth/auth-url", { params: { dc } });
      window.location.href = r.data.auth_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the Zoho connection");
    }
  };

  const disconnect = async () => {
    if (!confirm("Disconnect Zoho Books? Stored tokens will be cleared.")) return;
    await api.post("/zoho/disconnect", {});
    toast.success("Zoho Books disconnected");
    loadStatus();
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/zoho/sync", {});
      const s = r.data.summary || {};
      toast.success(`Synced · ${s.contacts || 0} contacts · ${s.items || 0} items · ${s.invoices || 0} invoices · ${s.chart_of_accounts || 0} accounts`);
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Zoho sync failed");
    } finally { setSyncing(false); }
  };

  const doPreview = async () => {
    if (!file) return toast.error("Choose a Zoho CSV export first");
    setUploading(true);
    try {
      const text = await file.text();
      const rows = parseCsv(text);
      if (!rows.length) throw new Error("CSV is empty or malformed");
      const r = await api.post("/zoho/preview-csv", { entity, rows });
      setPreview(r.data);
      toast.success(`Parsed ${rows.length} rows · ${r.data.stats?.matched || 0} matched, ${r.data.stats?.new || 0} new`);
    } catch (e) { toast.error(e?.response?.data?.detail || String(e.message || e)); }
    setUploading(false);
  };

  const downloadPreviewPDF = async () => {
    if (!preview?.parsed_token) return;
    try {
      const r = await api.post("/zoho/preview-pdf", { parsed_token: preview.parsed_token }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url;
      a.download = `zoho_migration_preview_${preview.entity}.pdf`;
      a.click(); URL.revokeObjectURL(url);
      toast.success("Preview PDF ready — share with your CA");
    } catch (e) { toast.error(e?.response?.data?.detail || String(e)); }
  };

  const doCommit = async () => {
    if (!preview?.parsed_token) return;
    setCommitting(true);
    setCommitProgress({ pct: 0, stage: "starting", note: "Submitting commit…" });
    let pollTimer = null;
    // Preemptively poll — backend allocates batch_id inside commit, so we begin
    // polling after the commit call resolves; still gives live pct updates for
    // large imports where the commit itself is slow.
    try {
      const r = await api.post("/zoho/commit-csv", {
        parsed_token: preview.parsed_token,
        include_opening_balances: includeOpening,   // Iter-121 · Detailed GL flag
      });
      const bid = r.data.batch_id;
      // One final progress poll for accurate pct/status
      try {
        const p = await api.get(`/zoho/progress/${bid}`);
        setCommitProgress({ pct: p.data.pct || 100, stage: p.data.stage || "done", note: p.data.note || "Commit complete" });
      } catch { /* fine */ }
      setResult(r.data);
      toast.success(`${preview.entity} · ${r.data.rows_staged} rows staged · batch ${bid.slice(0, 8)}`);
      setTimeout(() => setCommitProgress(null), 800);
    } catch (e) {
      setCommitProgress(null);
      toast.error(e?.response?.data?.detail || String(e));
    } finally {
      if (pollTimer) clearInterval(pollTimer);
      setCommitting(false);
    }
  };

  const reset = () => { setFile(null); setPreview(null); setResult(null); };

  // Iter-198 · Past Zoho Imports panel — list every batch with an Erase
  // button so users can wipe imported data whenever they want, without
  // having to keep the "Result" panel open (user report: data uploaded via
  // Zoho migrator was not getting erased).
  const [batches, setBatches] = useState([]);
  const loadBatches = async () => {
    try { setBatches((await api.get("/zoho/batches")).data || []); } catch { /* ignore */ }
  };
  useEffect(() => { loadBatches(); }, []);
  useEffect(() => { if (result) loadBatches(); }, [result]);
  const eraseZohoBatch = async (b) => {
    const msg = `Erase this Zoho import?\n\n` +
      `• Reverses every voucher posted for this batch\n` +
      `• Marks auto-created ledgers, dimensions & segments inactive\n` +
      `• Drops all staged rows for this batch\n\n` +
      `Audit trail is preserved (soft-delete). Continue?`;
    if (!window.confirm(msg)) return;
    try {
      const r = await api.delete(`/zoho/batches/${b.id}`);
      if (r.data.already_erased) toast.info("Already erased earlier");
      else toast.success(`Erased — ${r.data.erased.vouchers_reversed} vouchers reversed · ${r.data.erased.accounts} accounts · ${r.data.erased.segments} segments · ${r.data.erased.cost_centres} dimensions · ${r.data.erased.staged_rows} staged rows removed`);
      await loadBatches();
      if (result?.batch_id === b.id) setResult(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erase failed");
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="zoho-migrator-page">
      <PageHeader eyebrow="Migration Tools" title="Zoho Books Migrator"
        description="Two-stage CSV import — preview, review, then commit. Rows are staged for approval before hitting the ledger." />

      {/* OAuth / Direct Sync panel (kept for future — user opted to focus on CSV path) */}
      <Card className="p-5 rounded-sm border-2 border-accent/40 bg-accent/5" data-testid="zoho-oauth-card">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex-1 min-w-[300px]">
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-accent">
              <PlugZap className="w-3.5 h-3.5" /> Direct API sync
            </div>
            <div className="text-xl font-heading font-bold mt-1">Zoho Books · OAuth</div>
            {status?.connected ? (
              <div className="mt-2 text-sm flex items-center gap-2 text-emerald-700">
                <CheckCircle2 className="w-4 h-4" /> Connected via <b>accounts.zoho.{status.dc}</b>
                {status.last_sync_at && (
                  <span className="text-xs text-muted-foreground ml-2">
                    · last sync {new Date(status.last_sync_at).toLocaleString("en-IN")}
                  </span>
                )}
              </div>
            ) : status?.configured === false ? (
              <div className="mt-2 text-sm flex items-center gap-2 text-amber-700">
                <AlertTriangle className="w-4 h-4" /> Add this company's Zoho keys using the setup guide below
                (one-time, about 5 minutes). CSV upload further down works without any setup.
              </div>
            ) : (
              <div className="mt-2 text-sm text-muted-foreground">
                Not connected. Choose your Zoho data centre and click "Connect Zoho Books".
              </div>
            )}
            {status?.last_sync_summary && (
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                {Object.entries(status.last_sync_summary).map(([k, v]) => (
                  <div key={k} className="bg-white border border-accent/20 rounded-sm px-2 py-1.5">
                    <div className="text-[10px] font-mono uppercase text-muted-foreground">{k.replace(/_/g, " ")}</div>
                    <div className="font-bold text-accent">{v}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-end gap-2 flex-wrap">
            {!status?.connected && (
              <>
                <div>
                  <Label className="text-[10px] uppercase font-mono">Data centre</Label>
                  <Select value={dc} onValueChange={setDc}>
                    <SelectTrigger className="rounded-sm w-56 mt-1" data-testid="zoho-dc"><SelectValue /></SelectTrigger>
                    <SelectContent>{DCS.map((d) => <SelectItem key={d.v} value={d.v}>{d.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Button onClick={connect} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="zoho-connect" disabled={status?.configured === false}>
                  <Plug className="w-4 h-4" /> Connect Zoho Books
                </Button>
              </>
            )}
            {status?.connected && (
              <>
                <Button onClick={syncNow} disabled={syncing} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="zoho-sync">
                  <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing…" : "Sync Now"}
                </Button>
                <Button onClick={disconnect} variant="outline" className="rounded-sm gap-1.5" data-testid="zoho-disconnect">
                  <Unplug className="w-4 h-4" /> Disconnect
                </Button>
              </>
            )}
          </div>
        </div>
      </Card>

      {!status?.connected && (
        <ZohoSetupGuide defaultOpen={status?.configured === false}
          onSaved={(savedDc) => { setDc(savedDc); loadStatus(); }} />
      )}

      {/* Commit progress bar — visible while committing */}
      {commitProgress && (
        <div className="card-sharp p-4 border-l-4 border-l-accent" data-testid="zoho-commit-progress">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-sm font-medium">
              Committing… <span className="text-xs font-mono text-muted-foreground">{commitProgress.stage}</span>
            </div>
            <div className="text-sm font-mono font-bold text-accent">{commitProgress.pct}%</div>
          </div>
          <div className="h-2.5 bg-muted rounded-sm overflow-hidden">
            <div className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 transition-all"
                 style={{ width: `${commitProgress.pct}%` }} />
          </div>
          {commitProgress.note && <div className="text-[11px] text-muted-foreground mt-1.5">{commitProgress.note}</div>}
        </div>
      )}

      {/* Result panel */}
      {result && (
        <div className="card-sharp p-5 border-l-4 border-l-green-500" data-testid="zoho-commit-result">
          <div className="flex items-center gap-2 text-green-500 mb-3">
            <CheckCircle2 className="w-5 h-5" /><span className="font-semibold">Zoho import complete</span>
          </div>
          <div className="text-sm space-y-1">
            <div>Entity: <b>{result.entity}</b></div>
            <div>Rows staged: <span className="font-mono text-emerald-500">{result.rows_staged}</span></div>
            <div>Batch ID: <span className="font-mono text-xs">{result.batch_id}</span></div>
          </div>
          {/* Iter-201 · Promote contacts / customers / vendors to Party masters */}
          {["contacts", "customers", "vendors"].includes(result.entity) && (
            <div className="mt-4 rounded-sm border-l-4 border-emerald-500 bg-emerald-500/5 p-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 mb-1">
                Next step · Promote to Party Masters
              </div>
              <div className="text-xs text-foreground/80 mb-3 leading-relaxed">
                Staged rows carry every field Zoho exposes — <b>Name · GSTIN · PAN ·
                Email · Phone · Billing address · State · Pincode · Shipping address ·
                Credit terms · GST treatment · Opening balance</b>. Click <b>Promote</b>
                to create/update <code>{result.entity === "vendors" ? "Vendor" : result.entity === "customers" ? "Customer" : "Party"}</code>
                master records. Re-runs update existing rows by name — safe to
                re-import.
              </div>
              <Button size="sm" data-testid="zoho-promote-contacts-btn"
                        className="rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white gap-1.5"
                        onClick={async () => {
                          if (!confirm(`Promote ${result.rows_staged} row(s) into ${result.entity === "vendors" ? "Vendor" : result.entity === "customers" ? "Customer" : "Party"} masters?`)) return;
                          try {
                            const rr = await api.post(`/zoho/promote-contacts/${result.batch_id}`);
                            if (rr.data.status === "already_promoted") {
                              toast.info(`Already promoted at ${rr.data.promoted_at}`);
                            } else {
                              toast.success(`Created ${rr.data.parties_created} · Updated ${rr.data.parties_updated}${rr.data.parties_skipped ? ` · Skipped ${rr.data.parties_skipped}` : ""}`);
                            }
                          } catch (e) {
                            toast.error(e?.response?.data?.detail || "Promote failed");
                          }
                        }}>
                Promote to {result.entity === "vendors" ? "Vendors" : result.entity === "customers" ? "Customers" : "Party"} Master
              </Button>
            </div>
          )}

          {result.entity === "detailed_gl" && (            <div className="mt-4 rounded-sm border-l-4 border-blue-500 bg-blue-500/5 p-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-blue-700 mb-1">
                Next step · Promote to GL
              </div>
              <div className="text-xs text-foreground/80 mb-3 leading-relaxed">
                Staged rows are still in <code>zoho_stage</code>. Click <b>Promote</b> to
                auto-create missing ledger accounts, turn every reporting tag into
                both a Business Segment (for MIS) <b>and</b> a Dimension (for
                dimensional GL), and post vouchers using the mapping below.
                Rollback reverses every posted voucher.
              </div>
              {result.voucher_type_summary?.length > 0 && (
                <div className="mb-3">
                  <VoucherTypeMap
                    summary={result.voucher_type_summary}
                    value={vtypeMap}
                    onChange={setVtypeMap}
                    persistedAt={vtypeMapPersistedAt}
                    testidPrefix="zoho-vtype" />
                </div>
              )}
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" data-testid="zoho-promote-gl-btn"
                        className="rounded-sm bg-blue-600 hover:bg-blue-500 text-white gap-1.5"
                        onClick={async () => {
                          if (!confirm(`Promote batch ${result.batch_id.slice(0,8)}… to the GL?`)) return;
                          try {
                            const rr = await api.post(`/zoho/promote-detailed-gl/${result.batch_id}`,
                              { voucher_type_map: vtypeMap });
                            if (rr.data.status === "already_promoted") {
                              toast.info(`Already promoted at ${rr.data.promoted_at}`);
                            } else {
                              toast.success(
                                `Posted ${rr.data.vouchers_posted} vouchers · +${rr.data.accounts_created} accounts · +${rr.data.segments_created} segments · +${rr.data.cost_centres_created ?? 0} dimensions`
                              );
                              if (rr.data.failures?.length) {
                                toast.warning(`${rr.data.failures.length} groups failed — see logs`);
                              }
                              // Iter-195 · Persist so the next Zoho import auto-loads this map.
                              if (vtypeMap && Object.keys(vtypeMap).length) {
                                try {
                                  await api.put("/migrator/voucher-type-map/zoho", { map: vtypeMap });
                                  setVtypeMapPersistedAt(new Date().toISOString());
                                } catch { /* non-fatal */ }
                              }
                            }
                          } catch (e) {
                            toast.error(e?.response?.data?.detail || "Promote failed");
                          }
                        }}>
                  Promote to GL
                </Button>
                <Button size="sm" variant="outline" data-testid="zoho-rollback-gl-btn"
                        className="rounded-sm gap-1.5"
                        onClick={async () => {
                          if (!confirm(`Reverse all vouchers posted for batch ${result.batch_id.slice(0,8)}…?`)) return;
                          try {
                            const rr = await api.post(`/zoho/rollback-detailed-gl/${result.batch_id}`);
                            toast.success(`Reversed ${rr.data.reversed_count} voucher(s)`);
                          } catch (e) {
                            toast.error(e?.response?.data?.detail || "Rollback failed");
                          }
                        }}>
                  Rollback
                </Button>
              </div>
            </div>
          )}
          <Button variant="outline" onClick={reset} className="mt-4" data-testid="zoho-reset-btn">
            Migrate another file
          </Button>
          {/* Iter-198 · Erase this batch right here */}
          <Button variant="outline" onClick={() => eraseZohoBatch({ id: result.batch_id })} className="mt-4 ml-2 text-red-600 border-red-500/60 hover:bg-red-50 hover:border-red-500" data-testid="zoho-erase-this-batch-btn">
            Erase This Import
          </Button>
        </div>
      )}

      {/* Iter-198 · Past Zoho Imports — always visible when not previewing/committing. */}
      {!preview && !result && batches.length > 0 && (
        <div className="card-sharp p-5" data-testid="zoho-past-imports">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-accent">Past Zoho Imports · Erase to remove all data</div>
            <div className="text-[11px] text-muted-foreground">Erase reverses posted vouchers · marks auto-created ledgers, segments & dimensions inactive · drops staged rows.</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-[10px] font-mono uppercase text-muted-foreground">
                  <th className="text-left py-2 pr-3">Date</th>
                  <th className="text-left pr-3">Entity</th>
                  <th className="text-left pr-3">Batch ID</th>
                  <th className="text-right pr-3">Rows</th>
                  <th className="text-right pr-3">Posted vch</th>
                  <th className="text-center pr-3">Status</th>
                  <th className="text-right"></th>
                </tr>
              </thead>
              <tbody>
                {batches.map((b) => (
                  <tr key={b.id} className={`border-b ${b.status === "erased" ? "opacity-50" : ""}`} data-testid={`zoho-batch-${b.id.slice(0,8)}`}>
                    <td className="py-2 pr-3 text-xs">{b.created_at?.slice(0,16).replace("T", " ")}</td>
                    <td className="pr-3">{b.entity || "—"}</td>
                    <td className="pr-3 font-mono text-xs">{b.id.slice(0, 12)}…</td>
                    <td className="pr-3 text-right font-mono">{b.rows_staged ?? "—"}</td>
                    <td className="pr-3 text-right font-mono">{b.promoted_voucher_count ?? 0}</td>
                    <td className="pr-3 text-center">
                      <span className={`text-[10px] font-mono uppercase px-2 py-0.5 border ${b.status === "erased" ? "text-muted-foreground border-muted-foreground" : "text-green-500 border-green-500"}`}>
                        {b.status || (b.promoted_at ? "promoted" : "staged")}
                      </span>
                    </td>
                    <td className="text-right">
                      {b.status !== "erased" && (
                        <Button size="sm" variant="outline"
                                onClick={() => eraseZohoBatch(b)}
                                className="text-red-600 border-red-500/60 hover:bg-red-50"
                                data-testid={`zoho-erase-batch-${b.id.slice(0,8)}`}>
                          Erase
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Preview panel — Diff + PDF + Commit */}
      {!result && preview && (
        <div className="space-y-4" data-testid="zoho-preview">
          <div className="flex items-center justify-end -mb-2">
            <Button variant="outline" size="sm" onClick={downloadPreviewPDF}
                    className="rounded-sm gap-1.5" data-testid="zoho-download-preview-pdf-btn">
              <Download className="w-3.5 h-3.5" /> Download preview PDF · share with CA
            </Button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard label="Total rows" value={preview.stats?.total || 0} />
            <StatCard label="Matched" value={preview.stats?.matched || 0} tone="good" />
            <StatCard label="New" value={preview.stats?.new || 0} />
            <StatCard label="Warnings" value={preview.warnings?.length || 0} tone={preview.warnings?.length ? "warn" : ""} />
          </div>

          {preview.warnings?.length > 0 && (
            <div className="card-sharp p-4 border-l-4 border-l-orange-500" data-testid="zoho-warnings">
              <div className="flex items-center gap-2 text-orange-500 mb-2">
                <AlertTriangle className="w-4 h-4" /><span className="font-semibold text-sm">Warnings</span>
              </div>
              <ul className="text-sm list-disc pl-5">
                {preview.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          {/* Migration Comparison Diff */}
          <div className="card-sharp p-4" data-testid="zoho-diff-panel">
            <div className="flex items-center gap-2 mb-1">
              <ArrowLeftRight className="w-4 h-4 text-accent" />
              <div className="text-xs font-mono uppercase tracking-widest text-accent">
                Reconciliation Diff · Zoho values → Zenith after import
              </div>
            </div>
            <div className="text-[11px] text-muted-foreground mb-3">
              Side-by-side view of every {preview.entity} row from Zoho vs. what will land in
              Zenith. Use the filter to spot mismatches before you commit.
            </div>
            <DiffTable entity={preview.entity} mapping={preview.mapping_preview} />
            {preview.mapping_total > (preview.mapping_preview?.length || 0) && (
              <div className="mt-2 text-[10px] text-muted-foreground italic">
                Showing first {preview.mapping_preview.length} of {preview.mapping_total} rows.
                Full mapping is included in the downloadable PDF.
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <Button onClick={doCommit} disabled={committing} size="lg" data-testid="zoho-commit-btn">
              {committing ? "Committing…" : `Commit ${preview.stats?.total || 0} ${preview.entity} rows →`}
            </Button>
            <Button variant="outline" onClick={reset} data-testid="zoho-cancel-btn">
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Upload form (initial state) */}
      {!result && !preview && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card className="p-5 rounded-sm space-y-4" data-testid="zoho-csv-upload">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                CSV Upload · with Preview + Diff + Progress
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label>Entity to import</Label>
                  {/* Iter-202 · Download the exact CSV headers Zenith accepts */}
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/zoho/csv-template/${entity}.csv`}
                    download
                    className="text-[11px] font-mono uppercase tracking-widest text-accent hover:underline inline-flex items-center gap-1"
                    data-testid="zoho-download-template">
                    <Download className="w-3 h-3" /> Download template
                  </a>
                </div>
                <Select value={entity} onValueChange={setEntity}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="zm-entity"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ENTITIES.map((e) => <SelectItem key={e.v} value={e.v}>{e.l}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="text-[10px] text-muted-foreground mt-1 font-mono">
                  Template: headers Zenith reads + one sample row · edit &amp; upload
                </div>
              </div>
              <div>
                <Label>Zoho CSV Export</Label>
                <input type="file" accept=".csv" onChange={(e) => setFile(e.target.files?.[0])}
                  className="mt-1 block w-full text-sm" data-testid="zm-file" />
                {file && <div className="text-[11px] text-muted-foreground mt-1">
                  Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)
                </div>}
              </div>

              {/* Iter-121 · Detailed GL — opening-balance toggle */}
              {entity === "detailed_gl" && (
                <div className="rounded-sm border-l-4 border-amber-500 bg-amber-500/10 p-3 space-y-2" data-testid="zm-dgl-config">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-amber-700">
                    Detailed General Ledger · configuration
                  </div>
                  <div className="text-xs text-foreground/80 leading-relaxed">
                    Detailed GL contains <b>every</b> Zoho transaction — invoices,
                    bills, receipts, payments, JVs, and (optionally) the day-1
                    opening balances. Zenith stages each row as a journal line,
                    auto-creating any ledger not already in your Chart of Accounts.
                  </div>
                  <label className="flex items-start gap-2 cursor-pointer select-none">
                    <input type="checkbox" checked={includeOpening}
                              onChange={(e) => setIncludeOpening(e.target.checked)}
                              className="mt-1 rounded" data-testid="zm-dgl-include-opening" />
                    <span className="text-sm">
                      <b>Include opening balances</b>
                      <span className="text-xs text-muted-foreground block mt-0.5">
                        {includeOpening
                          ? "Rows flagged 'Opening Balance' in Zoho will be imported as opening TB entries."
                          : "Skip opening-balance rows (use this if you've already imported them via the separate 'Opening Balances' entity)."}
                      </span>
                    </span>
                  </label>
                </div>
              )}

              <Button onClick={doPreview} disabled={uploading || !file}
                className="rounded-sm bg-accent hover:bg-accent/90 text-white w-full gap-1.5"
                data-testid="zm-preview-btn">
                <Upload className="w-4 h-4" /> {uploading ? "Parsing…" : "Preview Migration"}
              </Button>
              <div className="text-[11px] italic text-muted-foreground">
                Nothing is written to the ledger until you approve rows on the review screen.
              </div>
            </Card>
          </div>

          <div className="card-sharp p-5 border-l-4 border-l-accent" data-testid="zoho-help">
            <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-3">
              How to export from Zoho Books
            </div>
            <ol className="text-sm space-y-2 list-decimal pl-5">
              <li>Log into <b>Zoho Books</b> → open the section you want to export
                  (<b>Contacts</b>, <b>Items</b>, <b>Sales → Invoices</b>, etc.).</li>
              <li>Click <b>More → Export</b> and choose <b>CSV</b> format with <i>all columns</i>.</li>
              <li>Zoho emails the CSV. Save it locally.</li>
              <li>Upload it here — Zenith will parse, match against your existing masters,
                  and give you a side-by-side reconciliation view before committing.</li>
            </ol>
            <div className="mt-3 text-[10px] text-muted-foreground italic">
              For <b>Opening Balances</b>, export the Trial Balance report as CSV with
              columns <code>Account Name · Debit · Credit</code>.
            </div>
            <div className="mt-2 text-[10px] text-muted-foreground italic">
              For <b>Detailed General Ledger</b>, in Zoho Books go to <b>Reports → Accountant → Detailed General Ledger</b>,
              set the date range to your full FY, and export as CSV. Columns should include
              <code> Date · Account · Reference · Debit · Credit · Transaction Type</code>.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
